
import i18n from 'i18next';
import { InitOptions } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';

// Import modular English translations
import enCommon from './locales/en/common.json';
import enHero from './locales/en/hero.json';
import enHeroActions from './locales/en/heroActions.json';
import enEarlyAccess from './locales/en/earlyAccess.json';
import enNavigation from './locales/en/navigation.json';
import enHowItWorks from './locales/en/howItWorks.json';
import enProperties from './locales/en/properties.json';
import enFooter from './locales/en/footer.json';
import enMisc from './locales/en/misc.json';
import enForDevelopers from './locales/en/forDevelopers.json';
import enAbout from './locales/en/about.json';
import enContact from './locales/en/contact.json';
import enCeo from './locales/en/ceo.json';

// Import modular Spanish translations
import esCommon from './locales/es/common.json';
import esHero from './locales/es/hero.json';
import esHeroActions from './locales/es/heroActions.json';
import esEarlyAccess from './locales/es/earlyAccess.json';
import esNavigation from './locales/es/navigation.json';
import esHowItWorks from './locales/es/howItWorks.json';
import esProperties from './locales/es/properties.json';
import esFooter from './locales/es/footer.json';
import esMisc from './locales/es/misc.json';
import esForDevelopers from './locales/es/forDevelopers.json';
import esAbout from './locales/es/about.json';
import esContact from './locales/es/contact.json';
import esCeo from './locales/es/ceo.json';

// Combine all English translations
const enTranslation = {
  common: enCommon,
  hero: enHero,
  heroActions: enHeroActions,
  earlyAccess: enEarlyAccess,
  nav: enNavigation,
  howItWorks: enHowItWorks,
  properties: enProperties.title ? enProperties : enProperties,
  filters: enProperties.filters || {},
  propertyGrid: enProperties.grid || {},
  propertyCard: enProperties.card || {},
  property: enProperties.property || {},
  footer: enFooter,
  forDevelopers: enForDevelopers,
  about: enAbout,
  contact: enContact,
  ceo: enCeo,
  ...enMisc
};

// Combine all Spanish translations
const esTranslation = {
  common: esCommon,
  hero: esHero,
  heroActions: esHeroActions,
  earlyAccess: esEarlyAccess,
  nav: esNavigation,
  howItWorks: esHowItWorks,
  properties: esProperties.title ? esProperties : esProperties,
  filters: esProperties.filters || {},
  propertyGrid: esProperties.grid || {},
  propertyCard: esProperties.card || {},
  property: esProperties.property || {},
  footer: esFooter,
  forDevelopers: esForDevelopers,
  about: esAbout,
  contact: esContact,
  ceo: esCeo,
  ...esMisc
};

// Enhanced language detection function - default to English
const detectLanguage = (): string => {
  // First priority: Check if user previously selected a language
  try {
    const storedLang = localStorage.getItem('propmatch-language');
    if (storedLang && (storedLang === 'en' || storedLang === 'es')) {
      console.log('Using stored language preference:', storedLang);
      return storedLang;
    }
  } catch (error) {
    console.warn('Error accessing localStorage:', error);
  }
  
  // Default to English for all new users
  return 'en';
};

// Initialize i18next configuration object
const i18nConfig: InitOptions = {
  resources: {
    en: { translation: enTranslation },
    es: { translation: esTranslation }
  },
  lng: detectLanguage(),
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false
  },
  react: {
    useSuspense: false,
    bindI18n: 'languageChanged loaded',
    bindI18nStore: 'added removed',
    nsMode: 'default'
  },
  saveMissing: false,
  missingKeyHandler: (lngs: readonly string[], ns: string, key: string, fallbackValue: string) => {
    console.warn(`Missing translation key: ${key} for language: ${lngs[0]} in namespace: ${ns}`);
  },
  detection: {
    order: ['localStorage', 'navigator'],
    lookupLocalStorage: 'propmatch-language',
    caches: ['localStorage'],
  }
};

// Initialize i18next with enhanced configuration
i18n
  .use(LanguageDetector)
  .use(Backend)
  .use(initReactI18next)
  .init(i18nConfig);

export default i18n;
